import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import DoodleLoader from '../components/Common/DoodleLoader/DoodleLoader';
import { registerLoadingCallback } from '../api/axios';

const LoadingContext = createContext();

export const LoadingProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const startTimeRef = useRef(0);
  const timeoutRef = useRef(null);
  const isInternalLoadingRef = useRef(false);

  const startLoader = useCallback((msg = '') => {
    if (isInternalLoadingRef.current) return;
    
    setMessage(msg || '');
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    startTimeRef.current = Date.now();
    setLoading(true);
    isInternalLoadingRef.current = true;
  }, []);

  const stopLoader = useCallback(() => {
    const elapsed = Date.now() - startTimeRef.current;
    const minimumDuration = 1000; // 1 second minimum
    const remaining = Math.max(0, minimumDuration - elapsed);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    timeoutRef.current = setTimeout(() => {
      setLoading(false);
      setMessage('');
      timeoutRef.current = null;
      isInternalLoadingRef.current = false;
    }, remaining);
  }, []);

  useEffect(() => {
    // Register the callback to sync with Axios interceptors
    registerLoadingCallback((isLoading) => {
      if (isLoading) {
        startLoader();
      } else {
        stopLoader();
      }
    });
  }, [startLoader, stopLoader]);

  // Manually show/hide for non-axios events if needed
  const showLoader = useCallback((msg) => startLoader(msg), [startLoader]);
  const hideLoader = useCallback(() => stopLoader(), [stopLoader]);

  return (
    <LoadingContext.Provider value={{ loading, showLoader, hideLoader }}>
      {children}
      {loading && <DoodleLoader message={message} />}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
